import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

export default function ClassManagement() {
  const { user } = useAuth();
  const role = user?.role;
  const canEdit = ['admin', 'superintendent', 'principal'].includes(role);

  const [classes, setClasses] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [campusFilter, setCampusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedClass, setSelectedClass] = useState(null);
  const [classStudents, setClassStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  // ─── Add / Edit class modal ─────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '', section: '', campus_id: '',
    teacher_id: '', academic_year: '2025-2026',
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cls, camp, tch, std] = await Promise.all([
        api.get('/classes/'),
        api.get('/campuses/').catch(() => ({ data: [] })),
        api.get('/users/?role=teacher').catch(() => ({ data: [] })),
        api.get('/students/').catch(() => ({ data: [] })),
      ]);
      setClasses(cls.data || []);
      setCampuses(camp.data || []);
      setTeachers(tch.data || []);
      setStudents(std.data || []);
    } catch (err) {
      console.error('Failed to load classes:', err);
    } finally {
      setLoading(false);
    }
  };

  // ─── Filtered classes ──────────────────────────────────
  const filteredClasses = useMemo(() => {
    return classes.filter(c => {
      if (campusFilter !== 'all' && c.campus_id !== parseInt(campusFilter)) return false;
      if (search) {
        const q = search.toLowerCase();
        const teacher = teachers.find(t => t.id === c.teacher_id);
        const blob = `${c.name} ${c.section || ''} ${teacher?.first_name || ''} ${teacher?.last_name || ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [classes, campusFilter, search, teachers]);

  // ─── Counts per campus ────────────────────────────────
  const campusCounts = useMemo(() => {
    return campuses.map(c => ({
      ...c,
      count: classes.filter(cls => cls.campus_id === c.id).length,
    }));
  }, [campuses, classes]);

  // ─── Helpers ───────────────────────────────────────────
  const getCampusName = (id) => campuses.find(c => c.id === id)?.name || '—';
  const getTeacher = (id) => teachers.find(t => t.id === id);
  const getStudentCount = (classId) => students.filter(s => s.class_id === classId).length;

  // ─── Open class details ────────────────────────────────
  const openClass = async (cls) => {
    setSelectedClass(cls);
    setLoadingStudents(true);
    try {
      // Use the student list we already fetched — no extra API call needed
      const list = students.filter(s => s.class_id === cls.id);
      setClassStudents(list);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingStudents(false);
    }
  };

  // ─── Add / Edit handlers ───────────────────────────────
  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setError('');

    if (!form.name || !form.campus_id) {
      setError('Class name and campus are required.');
      return;
    }

    setBusy(true);
    try {
      const payload = {
        name: form.name,
        section: form.section || null,
        campus_id: parseInt(form.campus_id),
        teacher_id: form.teacher_id ? parseInt(form.teacher_id) : null,
        academic_year: form.academic_year || null,
      };

      if (editing) {
        await api.put(`/classes/${editing.id}`, payload);
      } else {
        await api.post('/classes/', payload);
      }
      setModalOpen(false);
      resetForm();
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '', section: '', campus_id: '',
      teacher_id: '', academic_year: '2025-2026',
    });
    setEditing(null);
    setError('');
  };

  const openEdit = (cls) => {
    setEditing(cls);
    setForm({
      name: cls.name,
      section: cls.section || '',
      campus_id: cls.campus_id,
      teacher_id: cls.teacher_id || '',
      academic_year: cls.academic_year || '2025-2026',
    });
    setModalOpen(true);
  };

  const handleDeactivate = async (cls) => {
    if (!window.confirm(`Deactivate class "${cls.name}"? Students in this class will remain but the class won't be visible.`)) return;
    try {
      await api.delete(`/classes/${cls.id}`);
      fetchAll();
      if (selectedClass?.id === cls.id) setSelectedClass(null);
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const assignTeacher = async (cls, teacherId) => {
    try {
      await api.put(`/classes/${cls.id}`, {
        campus_id: cls.campus_id,
        name: cls.name,
        section: cls.section,
        teacher_id: teacherId ? parseInt(teacherId) : null,
        academic_year: cls.academic_year,
      });
      fetchAll();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div>
      {/* ═══════════ Header ═══════════ */}
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-bold">🏫 Class Management</h2>
        <div className="flex gap-2">
          <button onClick={fetchAll} disabled={loading}
            className="bg-blue-100 text-blue-700 px-3 py-2 rounded hover:bg-blue-200 text-sm">
            <i className="fas fa-sync-alt mr-1"></i> Refresh
          </button>
          {canEdit && (
            <button onClick={() => { resetForm(); setModalOpen(true); }}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm">
              <i className="fas fa-plus mr-1"></i> Add Class
            </button>
          )}
        </div>
      </div>

      {/* ═══════════ Stats ═══════════ */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="bg-white p-3 rounded shadow border-l-4 border-blue-500">
          <p className="text-xs text-gray-500">Total Classes</p>
          <p className="text-2xl font-bold text-blue-700">{classes.length}</p>
        </div>
        <div className="bg-white p-3 rounded shadow border-l-4 border-green-500">
          <p className="text-xs text-gray-500">With Teacher</p>
          <p className="text-2xl font-bold text-green-700">{classes.filter(c => c.teacher_id).length}</p>
        </div>
        <div className="bg-white p-3 rounded shadow border-l-4 border-yellow-500">
          <p className="text-xs text-gray-500">Without Teacher</p>
          <p className="text-2xl font-bold text-yellow-700">{classes.filter(c => !c.teacher_id).length}</p>
        </div>
        <div className="bg-white p-3 rounded shadow border-l-4 border-purple-500">
          <p className="text-xs text-gray-500">Total Students</p>
          <p className="text-2xl font-bold text-purple-700">{students.length}</p>
        </div>
      </div>

      {/* ═══════════ Campus cards (clickable filters) ═══════════ */}
      <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mb-4">
        <button
          type="button"
          onClick={() => setCampusFilter('all')}
          className={`text-left bg-white p-3 rounded shadow border-l-4 transition-all ${
            campusFilter === 'all'
              ? 'border-gray-700 ring-2 ring-gray-300'
              : 'border-gray-300 hover:border-gray-500'
          }`}
        >
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <i className="fas fa-building text-gray-700"></i> All Campuses
          </p>
          <p className="text-2xl font-bold text-gray-800">{classes.length}</p>
        </button>

        {campusCounts.map(c => {
          const isActive = campusFilter === String(c.id);
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setCampusFilter(prev => prev === String(c.id) ? 'all' : String(c.id))}
              className={`text-left bg-white p-3 rounded shadow border-l-4 transition-all ${
                isActive
                  ? 'border-blue-500 ring-2 ring-offset-1 ring-gray-300 bg-blue-50'
                  : 'border-blue-200 hover:border-blue-500 hover:shadow-md'
              }`}
            >
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <i className="fas fa-school text-blue-600"></i> {c.name}
              </p>
              <p className="text-2xl font-bold text-blue-700">{c.count}</p>
            </button>
          );
        })}
      </div>

      {/* ═══════════ Search ═══════════ */}
      <div className="bg-white p-3 rounded shadow mb-4 flex flex-wrap gap-3 items-center">
        <input
          type="text"
          placeholder="Search class, section, or teacher..."
          className="flex-1 min-w-[200px] p-2 border rounded text-sm"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <span className="text-sm text-gray-500">
          Showing {filteredClasses.length} of {classes.length}
        </span>
      </div>

      {/* ═══════════ Class grid ═══════════ */}
      {loading ? (
        <div className="bg-white p-6 rounded shadow text-center text-gray-500">Loading…</div>
      ) : filteredClasses.length === 0 ? (
        <div className="bg-white p-12 rounded shadow text-center text-gray-400">
          <i className="fas fa-chalkboard text-4xl mb-3"></i>
          <p>No classes found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {filteredClasses.map(cls => {
            const teacher = getTeacher(cls.teacher_id);
            const studentCount = getStudentCount(cls.id);
            const campusName = getCampusName(cls.campus_id);

            return (
              <div
                key={cls.id}
                className="bg-white p-4 rounded shadow border-l-4 border-blue-500 hover:shadow-md transition-all cursor-pointer group"
                onClick={() => openClass(cls)}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-bold text-lg text-gray-800 group-hover:text-blue-700">
                      {cls.name}
                    </h3>
                    {cls.section && (
                      <p className="text-xs text-gray-500">Section {cls.section}</p>
                    )}
                  </div>
                  {canEdit && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => { e.stopPropagation(); openEdit(cls); }}
                        className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                        title="Edit"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeactivate(cls); }}
                        className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded hover:bg-red-200"
                        title="Deactivate"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-1 mb-3">
                  <p className="text-xs text-gray-600 flex items-center gap-1">
                    <i className="fas fa-building text-gray-400"></i>
                    {campusName}
                  </p>
                  <p className="text-xs text-gray-600 flex items-center gap-1">
                    <i className="fas fa-chalkboard-teacher text-gray-400"></i>
                    {teacher ? `${teacher.first_name} ${teacher.last_name}` : (
                      <span className="text-yellow-600 font-medium">No teacher assigned</span>
                    )}
                  </p>
                </div>

                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="flex items-center gap-1 text-sm">
                    <i className="fas fa-user-graduate text-blue-500"></i>
                    <span className="font-bold text-blue-700">{studentCount}</span>
                    <span className="text-xs text-gray-500">student{studentCount !== 1 ? 's' : ''}</span>
                  </div>
                  <span className="text-xs text-blue-600 font-medium group-hover:underline">
                    View →
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══════════ Class details (students) modal ═══════════ */}
      {selectedClass && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedClass(null)}>
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div className="flex justify-between items-start p-5 border-b bg-gradient-to-r from-blue-50 to-white">
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  <i className="fas fa-chalkboard text-blue-600 mr-2"></i>
                  {selectedClass.name}
                  {selectedClass.section && (
                    <span className="text-base font-normal text-gray-500 ml-2">
                      · Section {selectedClass.section}
                    </span>
                  )}
                </h3>
                <div className="flex flex-wrap gap-3 text-xs text-gray-600 mt-1">
                  <span>
                    <i className="fas fa-building mr-1"></i>
                    {getCampusName(selectedClass.campus_id)}
                  </span>
                  <span>
                    <i className="fas fa-chalkboard-teacher mr-1"></i>
                    {getTeacher(selectedClass.teacher_id)
                      ? `${getTeacher(selectedClass.teacher_id).first_name} ${getTeacher(selectedClass.teacher_id).last_name}`
                      : 'No teacher assigned'}
                  </span>
                  <span>
                    <i className="fas fa-calendar mr-1"></i>
                    {selectedClass.academic_year || 'N/A'}
                  </span>
                </div>
              </div>
              <button onClick={() => setSelectedClass(null)}
                className="text-2xl text-gray-400 hover:text-gray-700 leading-none">×</button>
            </div>

            {/* Assign teacher (if editable) */}
            {canEdit && (
              <div className="px-5 py-3 border-b bg-gray-50 flex items-center gap-3 flex-wrap">
                <label className="text-sm font-medium text-gray-700">
                  <i className="fas fa-user-plus mr-1"></i> Class Teacher:
                </label>
                <select
                  value={selectedClass.teacher_id || ''}
                  onChange={(e) => {
                    assignTeacher(selectedClass, e.target.value);
                    setSelectedClass({
                      ...selectedClass,
                      teacher_id: e.target.value ? parseInt(e.target.value) : null,
                    });
                  }}
                  className="flex-1 max-w-xs p-2 border rounded text-sm bg-white"
                >
                  <option value="">— No teacher —</option>
                  {teachers.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.first_name} {t.last_name} ({t.email})
                    </option>
                  ))}
                </select>
                <span className="text-xs text-gray-500">
                  <i className="fas fa-info-circle"></i> Changing this saves immediately
                </span>
              </div>
            )}

            {/* Body — student list */}
            <div className="p-5 overflow-y-auto flex-1">
              {loadingStudents ? (
                <p className="text-gray-500 text-center py-8">Loading students…</p>
              ) : classStudents.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <i className="fas fa-user-graduate text-4xl mb-3"></i>
                  <p>No students in this class yet.</p>
                  <p className="text-xs mt-1">Use Bulk Upload or Students page to enroll.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-700">
                      <i className="fas fa-users mr-1"></i>
                      {classStudents.length} Student{classStudents.length !== 1 ? 's' : ''}
                    </h4>
                    <span className="text-xs text-gray-500">
                      Sorted by roll number
                    </span>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          {['#', 'Roll', 'Name', 'Reg #', 'Gender', 'Guardian', 'Phone', 'Status'].map(h => (
                            <th key={h} className="p-2 text-left font-semibold text-gray-700 text-xs">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[...classStudents]
                          .sort((a, b) => (a.roll_no || 999999) - (b.roll_no || 999999))
                          .map((s, i) => (
                            <tr key={s.id} className="border-t hover:bg-gray-50">
                              <td className="p-2 text-gray-400 text-xs">{i + 1}</td>
                              <td className="p-2">
                                <span className="px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-800">
                                  {s.roll_no || '—'}
                                </span>
                              </td>
                              <td className="p-2 font-medium">{s.student_name || 'Unknown'}</td>
                              <td className="p-2 text-xs text-gray-600 font-mono">{s.registration_number || '—'}</td>
                              <td className="p-2">
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                  s.gender === 'Male' ? 'bg-blue-100 text-blue-800'
                                  : s.gender === 'Female' ? 'bg-pink-100 text-pink-800'
                                  : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {s.gender || '—'}
                                </span>
                              </td>
                              <td className="p-2 text-xs text-gray-600">{s.guardian_name || '—'}</td>
                              <td className="p-2 text-xs text-gray-600">{s.guardian_phone || '—'}</td>
                              <td className="p-2">
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                  s.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  {s.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex justify-end">
              <button onClick={() => setSelectedClass(null)}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ Add / Edit Class Modal ═══════════ */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-bold">
                {editing ? 'Edit Class' : 'Add Class'}
              </h3>
              <button onClick={() => { setModalOpen(false); resetForm(); }}
                className="text-2xl text-gray-400 hover:text-gray-700">×</button>
            </div>

            <div className="p-4 space-y-3">
              {error && <div className="bg-red-50 text-red-600 p-2 rounded text-sm">{error}</div>}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Class Name *</label>
                  <input className="w-full p-2 border rounded"
                    placeholder="e.g., 5-A"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Section</label>
                  <input className="w-full p-2 border rounded"
                    placeholder="e.g., A"
                    value={form.section}
                    onChange={e => setForm({ ...form, section: e.target.value })} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Campus *</label>
                <select className="w-full p-2 border rounded"
                  value={form.campus_id}
                  onChange={e => setForm({ ...form, campus_id: e.target.value })}>
                  <option value="">— Select campus —</option>
                  {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Class Teacher</label>
                <select className="w-full p-2 border rounded"
                  value={form.teacher_id}
                  onChange={e => setForm({ ...form, teacher_id: e.target.value })}>
                  <option value="">— No teacher —</option>
                  {teachers.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.first_name} {t.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Academic Year</label>
                <input className="w-full p-2 border rounded"
                  placeholder="2025-2026"
                  value={form.academic_year}
                  onChange={e => setForm({ ...form, academic_year: e.target.value })} />
              </div>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
              <button onClick={() => { setModalOpen(false); resetForm(); }}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
                Cancel
              </button>
              <button onClick={handleSubmit} disabled={busy}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                {busy ? 'Saving…' : (editing ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}